import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Collapse,
  Alert,
  Box,
  Grid,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { 
  PRODUCTS_CREATE_ENDPOINT,
  PRODUCTS_UPDATE_ENDPOINT,
} from '../../config/urls';
import { useAuth } from '../../hooks/AuthProvider';
import typography from '../../cssUtils/typography';
import { ColorsConstants } from '../../cssUtils/constants';
import { ExpandMore } from '@mui/icons-material';
import ButtonOne from '../../common/normal-button';
import ButtonTwo from '../../common/dashboard-button';
import AppDropdown from '../../common/app-dropdown';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

const ProductsFormDialog = ({ 
    modalState, 
    handleModalState, 
    selectedClient, 
    allSegmentsDetails, 
    fetchData }) => {
  
  const { getValidToken } = useAuth();

  console.log("selectedClient: ", selectedClient);

  // New product
  const [newProduct, setNewProduct] = useState({
    content_id: '',
    title: '',
    description: '',
    item_price: 0,
    sale_price: 0,
    status: 'active', // default value
    availability: '' // default value
  });

  // Edit product
  const [editedProduct, setEditedProduct] = useState({
    title: '',
    description: '',
    item_price: 0,
    sale_price: 0,
    status: '',
    availability: ''
  });

  const handleNewProductChange = (field) => (event) => {
    setNewProduct(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };
  
  const handleProductChange = (field) => (event) => {
    setEditedProduct(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const [alertObj, setAlertObj] = useState({
    active: false,
    msg: "",
    type: ""
  });

  const handleAlertClose = () => {
    setAlertObj({
      active: false,
      type: "",
      msg: ""
    });
  };

  const handleClose = () => {
    handleModalState({ isOpen: false });
    handleAlertClose();

    setEditedProduct({
      title: '',
      description: '',
      item_price: 0,
      sale_price: 0,
      status: '',
      availability: ''
    });
    setNewProduct({
      content_id: '',
      title: '',
      description: '',
      item_price: 0,
      sale_price: 0,
      status: 'active', // default value
      availability: '' // default value
    });

    fetchData();
  };

  const handleActionUpdateSave = async() => {
    const apiUrl = `${PRODUCTS_UPDATE_ENDPOINT}${selectedClient.product_id}/`;

    console.log("API URL: ", apiUrl);

    const data = {
      "content_id": selectedClient.content_id,
      ...editedProduct
    };

    console.log("Payload: ", data);

    const token = await getValidToken();
    const tenant = localStorage.getItem('tenant');

    axios.put(apiUrl, data , {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        "X-Tenant-Id": tenant
      },
    })
      .then(response => {
        console.log('API Response:', response.data);
        setAlertObj({
          active: true,
          type: "success",
          msg: "Product Updated Successfully"
        });
        // handleClose();
      })
      .catch(error => {
        console.error('API Error:', error.response.data);
        setAlertObj({
          active: true,
          type: "error",
          msg: "Failed to Update Product"
        });
      });
  };

  const handleCreateProductSave = async() => {
    const apiUrl = PRODUCTS_CREATE_ENDPOINT;

    console.log("API URL: ", apiUrl);

    // Validation
    if (!newProduct.content_id.trim()) {
      setAlertObj({
        active: true,
        type: "error",
        msg: "Please enter a Content ID"
      });
      return;
    }

    if (!newProduct.title.trim()) {
      setAlertObj({
        active: true,
        type: "error",
        msg: "Please enter a title for the product"
      });
      return;
    }

    if (!newProduct.item_price || newProduct.item_price <= 0) {
      setAlertObj({
        active: true,
        type: "error",
        msg: "Please enter a valid item price"
      });
      return;
    }

    console.log("Payload: ", newProduct);

    const token = await getValidToken();
    const tenant = localStorage.getItem('tenant');

    axios.post(apiUrl, newProduct, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        "X-Tenant-Id": tenant
      },
    })
      .then(response => {
        console.log('Create Product API Response:', response.data);
        setAlertObj({
          active: true,
          type: "success",
          msg: "Product Created Successfully"
        });
        // Reset form after successful creation
        setNewProduct({
          content_id: '',
          title: '',
          description: '',
          item_price: 0,
          sale_price: 0,
          status: 'active',
          availability: ''
        });
      })
      .catch(error => {
        console.error('API Error:', error.response.data);
        setAlertObj({
          active: true,
          type: "error",
          msg: error.response.data.message || "Failed to Create Product"
        });
      });
  };

  // Options for dropdowns
  const statusOptions = [
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
  ];

  const availabilityOptions = [
    { label: "Select an option", value: "" },
    { label: "In Stock", value: "in_stock" },
    { label: "Out of Stock", value: "out_of_stock" },
    { label: "Coming Soon", value: "coming_soon" },
    { label: "Discontinued", value: "discontinued" },
  ];

  const addProductBody = () => {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Content ID */}
        <Box>
          <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, mb: 0.5 }}>
            Content ID: *
          </Typography>
          <TextField
            margin="dense"
            id="content_id"
            placeholder="Enter Content ID"
            type="text"
            fullWidth
            onChange={handleNewProductChange('content_id')}
            value={newProduct.content_id}
            required
            size="small"
            InputProps={{
              sx: typography.bodyLarge,
            }}
            InputLabelProps={{
              sx: typography.bodyLarge,
            }}
          />
        </Box>

        {/* Product Title */}
        <Box sx={{ position: 'relative' }}>
          <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, mb: 0.5 }}>
            Product Title: *
          </Typography>
          <TextField
            margin="dense"
            id="title"
            placeholder="Enter title"
            type="text"
            fullWidth
            onChange={handleNewProductChange('title')}
            value={newProduct.title}
            required
            size="small"
            InputProps={{
              sx: typography.bodyLarge,
              endAdornment: (
                <Typography 
                  sx={{ ...typography.bodySmall, color: 'gray' }}
                >
                  {newProduct.title.length}/50
                </Typography>
              ),
            }}
            InputLabelProps={{
              sx: typography.bodyLarge,
            }}
          />
        </Box>

        {/* Description */}
        <Box sx={{ position: 'relative' }}>
          <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, mb: 0.5 }}>
            Description:
          </Typography>
          <TextField
            margin="dense"
            id="description"
            placeholder="Enter description"
            multiline
            rows={4}
            type="text"
            fullWidth
            onChange={handleNewProductChange('description')}
            value={newProduct.description}
            size="small"
            InputProps={{
              sx: typography.bodyLarge,
              endAdornment: (
                <Typography 
                  sx={{ 
                    ...typography.bodySmall, 
                    color: 'gray',
                    position: 'absolute',
                    right: '10px',
                    top: '10px',
                  }}
                >
                  {newProduct.description.length}/250
                </Typography>
              ),
            }}
            InputLabelProps={{
              sx: typography.bodyLarge,
            }}
          />
        </Box>

        {/* Item Price */}
        <Box>
          <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, mb: 0.5 }}>
            Item Price: *
          </Typography>
          <TextField
            margin="dense"
            id="item_price"
            placeholder="Enter item price"
            type="number"
            fullWidth
            onChange={handleNewProductChange('item_price')}
            value={newProduct.item_price}
            required
            size="small"
            InputProps={{
              sx: typography.bodyLarge,
            }}
            InputLabelProps={{
              sx: typography.bodyLarge,
            }}
          />
        </Box>

        {/* Sale Price */}
        <Box>
          <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, mb: 0.5 }}>
            Sale Price:
          </Typography>
          <TextField
            margin="dense"
            id="sale_price"
            placeholder="Enter Sale price"
            type="number"
            fullWidth
            onChange={handleNewProductChange('sale_price')}
            value={newProduct.sale_price}
            size="small"
            InputProps={{
              sx: typography.bodyLarge,
            }}
            InputLabelProps={{
              sx: typography.bodyLarge,
            }}
          />
        </Box>

        {/* Status */}
        <Box>
          <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, mb: 0.5 }}>
            Status:
          </Typography>
          <AppDropdown
            menuItems={statusOptions}
            selectedValue={newProduct.status}
            onMenuSelect={(value) => handleNewProductChange('status')({ target: { value } })}
            variant="outlined"
            endIcon={<ExpandMore />}
            sx={{
              width: "100%",
              height: 40,
              textAlign: "left",
              justifyContent: "space-between",
            }}
          >
            <span style={{ ...typography.bodyLarge }}>
              {statusOptions.find(opt => opt.value === newProduct.status)?.label || "Select Status"}
            </span>
          </AppDropdown>
        </Box>

        {/* Availability */}
        <Box>
          <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, mb: 0.5 }}>
            Availability:
          </Typography>
          <AppDropdown
            menuItems={availabilityOptions}
            selectedValue={newProduct.availability}
            onMenuSelect={(value) => handleNewProductChange('availability')({ target: { value } })}
            variant="outlined"
            endIcon={<ExpandMore />}
            sx={{
              width: "100%",
              height: 40,
              textAlign: "left",
              justifyContent: "space-between",
            }}
          >
            <span style={{ ...typography.bodyLarge }}>
              {availabilityOptions.find(opt => opt.value === newProduct.availability)?.label || "Select an option"}
            </span>
          </AppDropdown>
        </Box>
      </Box>
    );
  };

  const updateProductBody = () => {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Title */}
        <Box sx={{ position: 'relative' }}>
          <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, mb: 0.5 }}>
            Title:
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            placeholder="Enter title"
            type="text"
            fullWidth
            onChange={handleProductChange('title')}
            value={editedProduct.title}
            required
            size="small"
            InputProps={{
              sx: typography.bodyLarge,
              endAdornment: (
                <Typography 
                  sx={{ ...typography.bodySmall, color: 'gray' }}
                >
                  {editedProduct.title.length}/50
                </Typography>
              ),
            }}
            InputLabelProps={{
              sx: typography.bodyLarge,
            }}
          />
        </Box>

        {/* Description */}
        <Box sx={{ position: 'relative' }}>
          <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, mb: 0.5 }}>
            Description:
          </Typography>
          <TextField
            margin="dense"
            id="description"
            placeholder="Enter description"
            multiline
            rows={4}
            type="text"
            fullWidth
            onChange={handleProductChange('description')}
            value={editedProduct.description}
            size="small"
            InputProps={{
              sx: typography.bodyLarge,
              endAdornment: (
                <Typography 
                  sx={{ 
                    ...typography.bodySmall, 
                    color: 'gray',
                    position: 'absolute',
                    right: '10px',
                    top: '10px',
                  }}
                >
                  {editedProduct.description.length}/250
                </Typography>
              ),
            }}
            InputLabelProps={{
              sx: typography.bodyLarge,
            }}
          />
        </Box>

        {/* Item Price */}
        <Box>
          <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, mb: 0.5 }}>
            Item Price:
          </Typography>
          <TextField
            margin="dense"
            id="item_price"
            placeholder="Enter item price"
            type="number"
            fullWidth
            onChange={handleProductChange('item_price')}
            value={editedProduct.item_price}
            required
            size="small"
            InputProps={{
              sx: typography.bodyLarge,
            }}
            InputLabelProps={{
              sx: typography.bodyLarge,
            }}
          />
        </Box>

        {/* Sale Price */}
        <Box>
          <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, mb: 0.5 }}>
            Sale Price:
          </Typography>
          <TextField
            margin="dense"
            id="sale_price"
            placeholder="Enter Sale price"
            type="number"
            fullWidth
            onChange={handleProductChange('sale_price')}
            value={editedProduct.sale_price}
            size="small"
            InputProps={{
              sx: typography.bodyLarge,
            }}
            InputLabelProps={{
              sx: typography.bodyLarge,
            }}
          />
        </Box>

        {/* Status - Dropdown */}
        <Box>
          <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, mb: 0.5 }}>
            Status:
          </Typography>
          <AppDropdown
            menuItems={statusOptions}
            selectedValue={editedProduct.status}
            onMenuSelect={(value) => handleProductChange('status')({ target: { value } })}
            variant="outlined"
            endIcon={<ExpandMore />}
            sx={{
              width: "100%",
              height: 40,
              textAlign: "left",
              justifyContent: "space-between",
            }}
          >
            <span style={{ ...typography.bodyLarge }}>
              {statusOptions.find(opt => opt.value === editedProduct.status)?.label || "Select Status"}
            </span>
          </AppDropdown>
        </Box>

        {/* Availability - Dropdown */}
        <Box>
          <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, mb: 0.5 }}>
            Availability:
          </Typography>
          <AppDropdown
            menuItems={availabilityOptions}
            selectedValue={editedProduct.availability}
            onMenuSelect={(value) => handleProductChange('availability')({ target: { value } })}
            variant="outlined"
            endIcon={<ExpandMore />}
            sx={{
              width: "100%",
              height: 40,
              textAlign: "left",
              justifyContent: "space-between",
            }}
          >
            <span style={{ ...typography.bodyLarge }}>
              {availabilityOptions.find(opt => opt.value === editedProduct.availability)?.label || "Select an option"}
            </span>
          </AppDropdown>
        </Box>
      </Box>
    );
  };

  useEffect(() => {
    if (selectedClient) {
      setEditedProduct({
        title: selectedClient.title || '',
        description: selectedClient.description || '',
        item_price: selectedClient.item_price || 0,
        sale_price: selectedClient.sale_price || 0,
        status: selectedClient.status || 'active',
        availability: selectedClient.availability || ''
      });
    }
  }, [selectedClient]);

  useEffect(() => {
    let timeoutId;
    
    if (alertObj.active) {
      timeoutId = setTimeout(() => {
          handleAlertClose();
      }, 5000);
    }
    
    // Cleanup function to clear the timeout when component unmounts or when alertObj changes
    return () => {
      if (timeoutId) {
          clearTimeout(timeoutId);
      }
    };
  }, [alertObj]);

  return (
    <BootstrapDialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={modalState.isOpen}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ m: 0, p: 2, position: 'relative' }} id="customized-dialog-title">
        <Typography sx={{ ...typography.h2 }}>
          {modalState.mode === 'add_product' ? "Add Product"
            : modalState.mode === 'update_product' ? "Edit Product"
            : ''}
        </Typography>

        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Collapse in={alertObj.active}>
          <Alert 
            sx={{ 
              mb: 2,
              "& .MuiAlert-message": typography.bodyLarge 
            }} 
            severity={alertObj.type} 
            onClose={handleAlertClose}
          >
            {alertObj.msg}
          </Alert>
        </Collapse>

        {modalState.mode === 'add_product' ? (
          addProductBody()
        ) : (modalState.mode === 'update_product') && (
          updateProductBody()
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2, gap: 1 }}>
        {modalState.mode === 'add_product' ? (
          <>
            <ButtonOne 
              variant="contained" 
              onClick={handleCreateProductSave}
              sx={typography.bodyLarge}
            >
              Save
            </ButtonOne>
            <ButtonTwo 
              variant="outlined" 
              onClick={handleClose}
              sx={typography.bodyLarge}
            >
              Close
            </ButtonTwo>
          </>
        ) : modalState.mode === 'update_product' ? (
          <>
            <ButtonOne 
              variant="contained" 
              onClick={handleActionUpdateSave}
              sx={typography.bodyLarge}
            >
              Save
            </ButtonOne>
            <ButtonTwo 
              variant="outlined" 
              onClick={handleClose}
              sx={typography.bodyLarge}
            >
              Close
            </ButtonTwo>
          </>
        ) : (
          <ButtonTwo 
            variant="outlined" 
            onClick={handleClose}
            sx={typography.bodyLarge}
          >
            Close
          </ButtonTwo>
        )}
      </DialogActions>
    </BootstrapDialog>
  );
}

export default ProductsFormDialog;